/**
 * AddressButton - Smart button that defers wallet bundle loading until user clicks
 *
 * This component shows a simple "Connect" button until clicked, preventing the
 * wallet-vendor bundle (640KB) from loading on initial page load.
 * Only loads AppKit when user actually needs wallet features.
 */
import { Button, ButtonGroup } from '@chakra-ui/react'
import { useState } from 'react'
import type { ComponentType } from 'react'

interface AddressButtonInnerProps {
  address: string | undefined
  isConnected: boolean
  isSignedIn: boolean
  borderRadius?: 'md' | 'none'
  width?: string
  onLogout?: () => void
  autoOpenModal?: boolean
  onModalOpened?: () => void
}

let AddressButtonInnerComponent: ComponentType<AddressButtonInnerProps> | null = null
let AddressButtonInnerPromise: Promise<ComponentType<AddressButtonInnerProps>> | null = null

const loadAddressButtonInner = async (): Promise<ComponentType<AddressButtonInnerProps>> => {
  if (AddressButtonInnerComponent) {
    return AddressButtonInnerComponent
  }
  if (AddressButtonInnerPromise) {
    return AddressButtonInnerPromise
  }

  AddressButtonInnerPromise = import('./AddressButtonInner').then(m => {
    AddressButtonInnerComponent = m.default
    return AddressButtonInnerComponent!
  })

  return AddressButtonInnerPromise
}

interface AddressButtonProps {
  address: string | undefined
  isConnected: boolean
  isSignedIn: boolean
  borderRadius?: 'md' | 'none'
  width?: string
  onLogout?: () => void
}

const AddressButton = ({
  address,
  isConnected,
  isSignedIn,
  onLogout,
  borderRadius = 'md',
  width,
}: AddressButtonProps) => {
  const [appKitReady, setAppKitReady] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [userInitiated, setUserInitiated] = useState(false)

  // Only load AppKit when user clicks (either Connect or View Account)
  const handleClick = async () => {
    if (appKitReady || isLoading) {
      console.log('[AddressButton] Click ignored - already ready or loading', {
        appKitReady,
        isLoading,
      })
      return
    }

    console.log('[AddressButton] Starting AppKit load...')
    setUserInitiated(true)
    setIsLoading(true)
    try {
      // Load AppKit AND AddressButtonInner on-demand when user clicks
      const [appKitModule] = await Promise.all([
        import('../utils/appkit-init'),
        loadAddressButtonInner(),
      ])

      console.log('[AddressButton] AppKit module loaded, initializing...')
      await appKitModule.initializeAppKit()
      console.log('[AddressButton] AppKit initialized, setting ready state')
      setAppKitReady(true)
      // Don't clear loading state here - let onModalOpened clear it
    } catch (error) {
      console.error('[AddressButton] Failed to load AppKit:', error)
      setUserInitiated(false)
      setIsLoading(false)
    }
  }

  // Show simple button until user clicks
  if (!appKitReady || !AddressButtonInnerComponent) {
    const buttonText = isConnected ? 'View Account' : 'Connect'

    return (
      <ButtonGroup isAttached borderRadius={borderRadius}>
        <Button
          variant="flame"
          size="md"
          onClick={handleClick}
          isLoading={isLoading}
          loadingText="Loading..."
          width={width}
        >
          {buttonText}
        </Button>
      </ButtonGroup>
    )
  }

  // Render full wallet UI once loaded
  const InnerComponent = AddressButtonInnerComponent
  return (
    <InnerComponent
      address={address}
      isConnected={isConnected}
      isSignedIn={isSignedIn}
      borderRadius={borderRadius}
      width={width}
      onLogout={onLogout}
      autoOpenModal={userInitiated}
      onModalOpened={() => {
        console.log('[AddressButton] onModalOpened called - clearing loading state')
        setUserInitiated(false)
        setIsLoading(false)
      }}
    />
  )
}

export default AddressButton
